import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { BuilderCard, BuilderCardHeader } from '@components/BuilderCard';
import TranslateIcon from '@mui/icons-material/TranslateRounded';
import { Select } from '@components/Select';
import { useTranslation } from '@hooks';
import MicrositeFileUploader from '@features/MicrositeFileUploader';
import { getLinkTypeFlags, getLinkReferenceError, isLinkIncompatible } from './helpers';

const Form = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: 16,
    margin: '8px 0',
    paddingTop: 8,
    '@media (max-width: 1200px)': {
        gridTemplateColumns: '100%',
    },
    '@media (max-width: 768px)': {
        gridTemplateColumns: '100%',
    },
});

const MicrositeLink = ({
    listeners,
    attributes,
    isView,
    linkContext,
    link,
    isOpen,
    hasError,
    index,
    isUploadingFile,
    linkClick,
    onChangeDefaultLink,
    onChangeReference,
    onChangeFile,
    setLocalizeModal,
    onClickDelete,
}) => {
    const { t } = useTranslation();

    const { isBrand, surveysOptions, micrositeOptions, menusOptions, formsOptions } = linkContext;
    const { isSurvey, isMicrosite, isMenu, isForm, isFile, isLink } = getLinkTypeFlags(link);
    const isIncompatible = isLinkIncompatible(link, isBrand);
    const referenceError = getLinkReferenceError(link, linkContext);

    const getLinkTypeLabel = () => {
        if (isSurvey) return t('common.survey');
        if (isMicrosite) return t('common.microsite');
        if (isMenu) return t('common.menu');
        if (isForm) return t('common.form');
        if (isFile) return t('common.file');
        return t('common.link');
    };

    const expanded = isOpen || !!referenceError;

    return (
        <BuilderCard isExpanded={expanded} hasError={hasError}>
            <BuilderCardHeader
                caption={getLinkTypeLabel()}
                title={link?.title}
                isExpanded={expanded}
                hasError={hasError}
                onToggle={() => linkClick(+index)}
                listeners={listeners}
                attributes={attributes}
            />
            {expanded ? (
                <>
                    {referenceError && (
                        <Typography
                            variant="caption"
                            sx={{ color: 'var(--error-main)', display: 'block', marginBottom: '8px' }}
                        >
                            {t(referenceError)}
                        </Typography>
                    )}
                    <Form>
                        <Input
                            inputProps={{
                                name: 'title',
                                value: link?.title,
                                onChange: e => onChangeDefaultLink(e, index, link?.language),
                            }}
                            label={t('common.title')}
                            error={!link?.title}
                            helperText={!link?.title ? t('validation.required') : ''}
                            fullWidth
                            required
                            disabled={isView || isIncompatible}
                        />
                        {isSurvey && (
                            <Select
                                onChange={e => onChangeReference(e, index, 'surveyId')}
                                label={t('common.survey')}
                                fullWidth
                                value={link.surveyId}
                                options={surveysOptions}
                                disabled={isView || isIncompatible}
                                error={!!referenceError}
                            />
                        )}
                        {isMicrosite && (
                            <Select
                                onChange={e => onChangeReference(e, index, 'linkedMicrositeId')}
                                label={t('common.microsite')}
                                fullWidth
                                value={link.linkedMicrositeId}
                                options={micrositeOptions}
                                disabled={isView || isIncompatible}
                                error={!!referenceError}
                            />
                        )}
                        {isMenu && (
                            <Select
                                onChange={e => onChangeReference(e, index, 'menuId')}
                                label={t('common.menu')}
                                fullWidth
                                value={link.menuId}
                                options={menusOptions}
                                disabled={isView}
                                error={!!referenceError}
                            />
                        )}
                        {isForm && (
                            <Select
                                onChange={e => onChangeReference(e, index, 'formId')}
                                label={t('common.form')}
                                fullWidth
                                value={link.formId}
                                options={formsOptions}
                                disabled={isView}
                                error={!!referenceError}
                            />
                        )}
                        {isLink && (
                            <Input
                                inputProps={{
                                    name: 'link',
                                    value: link?.link,
                                    onChange: e => onChangeDefaultLink(e, index, link?.language),
                                }}
                                label={t('common.link')}
                                error={!link?.link}
                                helperText={!link?.link ? t('validation.required') : ''}
                                fullWidth
                                disabled={isView}
                            />
                        )}
                        {isFile && (
                            <MicrositeFileUploader
                                item={link}
                                error={!link?.fileUrl}
                                disabled={isView || isUploadingFile}
                                onChange={e => onChangeFile(e, index, link?.language)}
                            />
                        )}
                    </Form>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<TranslateIcon />}
                            onClick={() =>
                                setLocalizeModal({
                                    open: true,
                                    index,
                                    ctx: { isSurvey, isFile, isMicrosite, isMenu, isForm },
                                })
                            }
                            disabled={isView}
                        >
                            {t('buttons.localization')}
                        </Button>
                        <div>
                            <Button
                                variant="danger"
                                size="large"
                                onClick={() => onClickDelete(index)}
                                disabled={isView}
                            >
                                {t('buttons.delete')}
                            </Button>
                        </div>
                    </div>
                </>
            ) : null}
        </BuilderCard>
    );
};

export default MicrositeLink;
